import React from 'react';
import 'twin.macro';
import { StackListItem } from 'components/StackList/StackListItem';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  EllipsisHorizontalIcon,
  FolderOpenIcon,
} from '@heroicons/react/24/outline';
import { DraggableOverlay } from 'components/StackList/DraggableOverlay';
import { Action } from './types';
import { Menu } from 'components/Menu';
import { useTranslation } from 'react-i18next';

export interface Item<T> {
  id: string;
  title: string;
  isFolder?: boolean;
  icon?: React.ReactNode;
  disabledOver?: (over?: T) => boolean;
}

interface Props<T> {
  items: T[];
  onClick: (item: T) => void;
  onDragEnd?: ({ item, overId }: { item: T; overId: string }) => void;
  actionsFct: (item: T) => Action<T>[];
}

export function StackList<T extends Item<T>>(props: Props<T>) {
  const { items } = props;
  const { t } = useTranslation(['common']);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        tolerance: 10,
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        tolerance: 10,
        delay: 200,
      },
    }),
  );

  if (items.length === 0) {
    return (
      <div>
        <div tw="text-center">
          <FolderOpenIcon tw="mx-auto h-24 w-24 text-neutral-quiet mb-2" />
          <h2 tw="mb-6 text-2xl tracking-tight text-muted">
            {t('common:components.stackList.emptyFolder')}
          </h2>
        </div>
      </div>
    );
  }
  return (
    <DndContext
      sensors={sensors}
      onDragEnd={e => {
        const sourceItem = items.find(i => i.id === e.active.id);
        if (!sourceItem) {
          return;
        }
        if (props.onDragEnd && e?.over?.id) {
          return props.onDragEnd({
            item: sourceItem,
            overId: e?.over?.id as string,
          });
        }
      }}
    >
      <ul role="list" tw="space-y-4 pb-2">
        {items.map(col => (
          <StackListItem
            id={col.id}
            key={col.id}
            onClick={() => {
              props.onClick(col);
            }}
            title={col.title}
            disabledOver={col.disabledOver}
            icon={col.icon}
          >
            <Menu portal>
              <Menu.Button
                aria-label={`Actions ${col.title}`}
                minimal
                icon={EllipsisHorizontalIcon}
              />
              <Menu.Items>
                {(props.actionsFct(col) || []).map(action => (
                  <Menu.Item
                    key={action.title}
                    intent={action.intent}
                    onClick={() => action.onClick(col)}
                    text={action.title}
                    icon={action.icon}
                  />
                ))}
              </Menu.Items>
            </Menu>
          </StackListItem>
        ))}
      </ul>
      <DraggableOverlay />
    </DndContext>
  );
}
